/**
 * The seam between the two auth providers.
 *
 * Everything above this module — the store, the screens, the API client — deals in
 * "there is a session" and "here are the headers". Which provider produced the
 * credential, and whether it needed a custom-token exchange, is decided here and
 * nowhere else.
 */

import { clearToken, getToken, setToken } from "~/lib/auth-token";
import {
  exchangeCustomToken,
  firebaseSignOut,
  getFreshIdToken,
} from "~/lib/firebase";
import type { AuthProvider, SessionResponse } from "~/types/auth";

let provider: AuthProvider = "dev";

/** Record which provider the API reported from `/auth/config`. */
export function setAuthProvider(next: AuthProvider): void {
  provider = next;
}

export function getAuthProvider(): AuthProvider {
  return provider;
}

/**
 * Turn a successful OTP verification into a live session.
 *
 * A `firebase_custom` token is not a credential the API accepts — it must be traded
 * with Firebase for an ID token first. A `bearer` token is already the credential.
 */
export async function establishSession(
  session: SessionResponse,
): Promise<void> {
  if (session.token_type === "firebase_custom") {
    setAuthProvider("firebase");
    await setToken(await exchangeCustomToken(session.token));
    return;
  }
  setAuthProvider("dev");
  await setToken(session.token);
}

/** Headers for an authenticated request. */
export async function authHeaders(): Promise<Record<string, string>> {
  if (provider === "firebase") {
    // The SDK holds the authoritative, auto-refreshing ID token and restores it
    // asynchronously at start-up. There is deliberately no fallback to the stored
    // copy: a Firebase ID token lives an hour, so the stored one is only ever stale
    // and sending it would turn every cold start into a forced sign-out.
    const fresh = await getFreshIdToken();
    return fresh ? { Authorization: `Bearer ${fresh}` } : {};
  }

  const token = getToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function endSession(): Promise<void> {
  await clearToken();
  if (provider === "firebase") {
    try {
      await firebaseSignOut();
    } catch {
      // The local token is already gone, which is what actually signs the user out.
    }
  }
}
